import { useMemo, useState, type ReactNode } from 'react'
import { Autocomplete, Button, Card, Chip, Description, Label, ListBox, SearchField, Switch, useFilter } from '@heroui/react'
import { Icon, type IconName } from '../ui/icons'
import { BarChart, Donut, HBars, Sparkline, WaitChart, type HBar } from '../ui/LiveCharts'
import { useStaffText } from '../ui/StaffText'
import { kpiWait } from '../lib/floorBoard'
import { shareKpiLevel, waitKpiLevel, type Attention, type DashSeries, type DeskCounts, type Freshness, type KpiLevel, type StatusKind } from '../lib/liveDash'
import { formatMoment } from '../lib/staffText'
import type { StaffKey } from '../lib/staffText'

// The Live dashboard's shared parts: the head, the KPI row, the chart and Needs attention.

type Say = ReturnType<typeof useStaffText>['say']

/** Title and one line; the Live chip and the branch picker on the right. */
export function DashHead({ sub, children }: { sub: string; children: ReactNode }) {
  const { say } = useStaffText()
  return (
    <header className="fl-head">
      <div className="fl-head__text">
        <h1 className="fl-title">{say('floor.db.title')}</h1>
        <p className="fl-lead">{sub}</p>
      </div>
      <div className="fl-tools">{children}</div>
    </header>
  )
}

/** A card's title as an h2, so the page's headings run h1, h2. */
export function SectionTitle({ id, tip, children }: { id: string; tip?: string; children: ReactNode }) {
  return (
    <Card.Title id={id} title={tip} render={(props) => <h2 {...props} />}>
      {children}
    </Card.Title>
  )
}

const FRESH_COLOR = { live: 'success', connecting: 'warning', offline: 'danger', paused: 'default' } as const

/** Live while the data arrives, else what stands in its way. */
export function LiveChip({ fresh, zone }: { fresh: Freshness; zone?: string }) {
  const { lang, say } = useStaffText()
  const text =
    fresh.kind === 'paused'
      ? say('floor.db.updatedAt', { time: formatMoment(new Date(fresh.at), lang, { time: true, zone }) })
      : say(`floor.db.${fresh.kind}` as StaffKey)
  return (
    <Chip className="fl-live" size="lg" variant="soft" color={FRESH_COLOR[fresh.kind]} data-kind={fresh.kind} role="status">
      <i aria-hidden="true" />
      <Chip.Label>{text}</Chip.Label>
    </Chip>
  )
}

const ALL = 'all'

/** Auto-refresh and Refresh: off holds the page as it was until Refresh. */
export function RefreshControls({ auto, onAuto, onRefresh }: { auto: boolean; onAuto: (on: boolean) => void; onRefresh: () => void }) {
  const { say } = useStaffText()
  return (
    <>
      <Switch className="fl-auto" size="sm" isSelected={auto} onChange={onAuto}>
        <Switch.Content>
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Label>{say('floor.db.auto')}</Label>
        </Switch.Content>
      </Switch>
      <Button size="sm" variant="secondary" onPress={onRefresh}>
        <Icon name="refresh" size={14} />
        {say('floor.db.refresh')}
      </Button>
    </>
  )
}

/** The branch picker: All branches first, then every branch by name with its waiting count, and a search. */
export function BranchPicker({
  branches,
  current,
  allWaiting,
  onChoose,
}: {
  branches: { branchId: string; name: string; waiting: number; overTarget: number }[]
  current: string
  allWaiting: number
  onChoose: (branchId: string) => void
}) {
  const { say } = useStaffText()
  const { contains } = useFilter({ sensitivity: 'base' })
  // Typed words go when the list closes, so it opens on every branch again.
  const [find, setFind] = useState('')
  const options = useMemo(
    () => [
      { branchId: '', name: say('floor.all'), waiting: allWaiting, overTarget: 0 },
      ...[...branches].sort((a, b) => a.name.localeCompare(b.name)),
    ],
    [branches, allWaiting, say],
  )
  return (
    <Autocomplete
      className="fl-picker"
      aria-label={say('common.branch')}
      variant="secondary"
      selectionMode="single"
      onOpenChange={(open) => !open && setFind('')}
      value={current || ALL}
      onChange={(v) => v != null && onChoose(v === ALL ? '' : String(v))}
    >
      <Autocomplete.Trigger>
        <Autocomplete.Value />
        <Autocomplete.Indicator />
      </Autocomplete.Trigger>
      <Autocomplete.Popover>
        <Autocomplete.Filter filter={contains} inputValue={find} onInputChange={setFind}>
          <SearchField autoFocus aria-label={say('floor.find')} variant="secondary">
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={say('floor.db.searchN', { n: branches.length })} />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
        <ListBox aria-label={say('floor.branches')} renderEmptyState={() => <p className="fl-quiet">{say('floor.db.noMatch')}</p>}>
          {options.map((o) => (
            <ListBox.Item key={o.branchId || ALL} id={o.branchId || ALL} textValue={o.name}>
              <span className="fl-picker__opt">
                <Label>{o.name}</Label>
                <Description className={o.overTarget > 0 ? 'fl-over' : undefined}>
                  {say('hall.tile.waiting', { n: o.waiting })}
                  {o.overTarget > 0 && <> · {say('floor.db.overSuffix', { n: o.overTarget })}</>}
                </Description>
              </span>
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
        </Autocomplete.Filter>
      </Autocomplete.Popover>
    </Autocomplete>
  )
}

interface Tile {
  key: string
  label: string
  tip: string
  value: string
  unit?: string
  note: string
  /** Only cards with a target carry a status colour. */
  level?: KpiLevel
  spark: number[]
}

const LEVEL_CHIP = { ok: 'success', near: 'warning', over: 'danger' } as const
const LEVEL_WORD = { ok: 'floor.db.s.onTrack', near: 'floor.db.s.near', over: 'floor.db.s.over' } as const

/** The six figures across the top, each with its last four hours. */
export function KpiRow(props: {
  waiting: number
  served: number | null
  avgWaitSec: number | null
  avgTxSec: number | null
  longestSec: number | null
  /** Customers waiting past their own service target, and close to it. */
  over: number
  near: number
  /** The worst queue's average wait against its target. */
  avgWaitLevel: KpiLevel
  desksOpen: number
  series: DashSeries
}) {
  const { lang, say } = useStaffText()
  const wait = (s: number | null) => (s === null ? '—' : kpiWait(s, lang))
  const s = props.series
  const sla = s.slaNow
  const tiles: Tile[] = [
    {
      key: 'waiting', label: say('hall.waiting'), tip: say('floor.db.tip.waiting'), value: String(props.waiting),
      note: say('floor.db.k.desksOpen', { n: props.desksOpen }), spark: s.waiting,
    },
    {
      key: 'avgWait', label: say('floor.db.k.avgWait'), tip: say('floor.db.tip.avgWait'), value: wait(props.avgWaitSec),
      note: say('floor.db.c.avgWait'), level: props.avgWaitLevel, spark: s.avgWaitMin,
    },
    {
      key: 'longest', label: say('floor.db.k.longest'), tip: say('floor.db.tip.longest'), value: wait(props.longestSec),
      note: props.over > 0 ? say('floor.db.s.overN', { n: props.over }) : say('floor.db.c.longest'),
      level: waitKpiLevel(props.over, props.near), spark: s.longestMin,
    },
    {
      key: 'served', label: say('hall.servedToday'), tip: say('floor.db.tip.served'), value: props.served === null ? '—' : String(props.served),
      note: say('floor.db.c.served'), spark: s.servedRunning,
    },
    {
      key: 'avgTx', label: say('floor.db.k.avgTx'), tip: say('floor.db.tip.avgTx'), value: wait(props.avgTxSec),
      note: say('floor.db.c.avgTx'), spark: s.avgTxMin,
    },
    {
      key: 'sla', label: say('floor.db.k.sla'), tip: say('floor.db.tip.sla'), value: sla === null ? '—' : String(sla), unit: sla === null ? undefined : '%',
      note: say('floor.db.c.sla'), level: shareKpiLevel(sla), spark: s.slaPct,
    },
  ]
  return (
    <Card className="fl-kpis" role="group" aria-label={say('floor.db.title')}>
      {tiles.map((t) => (
        <div key={t.key} className="fl-kpi" data-key={t.key} data-level={t.level} title={t.tip}>
          <span className="fl-kpi__head">
            <span className="fl-kpi__label">{t.label}</span>
            {t.level && (
              <Chip size="sm" variant="soft" color={LEVEL_CHIP[t.level]}>
                {say(LEVEL_WORD[t.level])}
              </Chip>
            )}
          </span>
          <b className="fl-kpi__value">
            {t.value}
            {t.unit && <small>{t.unit}</small>}
          </b>
          <span className="fl-kpi__note">{t.note}</span>
          <Sparkline values={t.spark} />
        </div>
      ))}
    </Card>
  )
}

export type AttentionLevel = 'critical' | 'serious' | 'warning' | 'good'

export interface AttentionItem {
  key: string
  /** For a link that scrolls to the item (an approval email's request). */
  domId?: string
  highlight?: boolean
  level: AttentionLevel
  icon: IconName
  title: ReactNode
  sub?: ReactNode
  action?: ReactNode
}

/** What needs a supervisor now, worst first. */
export function AttentionCard({ items }: { items: AttentionItem[] }) {
  const { say } = useStaffText()
  return (
    <Card className="fl-attn" role="region" aria-labelledby="fl-attn">
      <Card.Header className="fl-card-h">
        <SectionTitle id="fl-attn">{say('floor.db.attention')}</SectionTitle>
        {items.length > 0 && <Chip size="sm">{items.length}</Chip>}
      </Card.Header>
      {items.length === 0 ? (
        <p className="fl-quiet">{say('floor.db.allClear')}</p>
      ) : (
        <ul className="fl-attn__list">
          {items.map((a) => (
            <li key={a.key} id={a.domId} className="fl-alert" data-level={a.level} data-highlight={a.highlight || undefined}>
              <Icon name={a.icon} size={16} />
              <span className="fl-alert__text">
                <b>{a.title}</b>
                {a.sub && <small>{a.sub}</small>}
              </span>
              {a.action}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

const ATTENTION_ICON: Record<Attention['kind'], IconName> = { noStaff: 'alert', noDesk: 'alert', over: 'clock', near: 'clock', long: 'user' }

/** lib/liveDash's list in words; `onShow` adds Show to the over-target line. */
export function attentionItems(list: Attention[], say: Say, onShow?: () => void): AttentionItem[] {
  return list.map((a) => {
    const base = { key: a.kind + ('name' in a ? a.name : ''), level: a.level, icon: ATTENTION_ICON[a.kind] }
    switch (a.kind) {
      case 'noStaff':
        return { ...base, title: say('floor.db.a.noStaff', { name: a.name, n: a.waiting }), sub: say('floor.db.a.noStaffSub') }
      case 'noDesk':
        return { ...base, title: say('floor.db.a.noDesk', { name: a.name, n: a.waiting }), sub: say('floor.db.a.noDeskSub') }
      case 'over':
        return {
          ...base,
          title: say('floor.db.a.over', { n: a.n }),
          sub: say('floor.db.a.overSub'),
          action: onShow ? <ActButton onPress={onShow}>{say('floor.db.a.show')}</ActButton> : undefined,
        }
      case 'near':
        return { ...base, title: say('floor.db.a.near', { n: a.n }), sub: say('floor.db.a.nearSub') }
      case 'long':
        return { ...base, title: say('floor.db.a.long', { n: a.n }), sub: a.who.join(', ') }
    }
  })
}

/** A small quiet button on an attention line. */
export function ActButton({ onPress, children }: { onPress: () => void; children: ReactNode }) {
  return (
    <Button size="sm" variant="ghost" onPress={onPress}>
      {children}
    </Button>
  )
}

const STATUS_COLOR = { noStaff: 'danger', over: 'danger', near: 'warning', onTrack: 'success' } as const

/** The status a branch row carries. */
export function StatusChip({ kind, n }: { kind: StatusKind; n?: number }) {
  const { say } = useStaffText()
  const text =
    kind === 'over' && n !== undefined
      ? say('floor.db.s.overN', { n })
      : say(({ noStaff: 'floor.db.s.noStaff', over: 'floor.db.s.over', near: 'floor.db.s.near', onTrack: 'floor.db.s.onTrack' } as const)[kind])
  return (
    <Chip size="sm" variant="soft" color={STATUS_COLOR[kind]}>
      {text}
    </Chip>
  )
}

/** The time under each point, in the branch's zone. */
export function useTimes(ends: string[], zone?: string): string[] {
  const { lang } = useStaffText()
  return useMemo(() => ends.map((e) => formatMoment(e, lang, { time: true, zone })), [ends, lang, zone])
}

/** Waiting over the last hours beside Needs attention. `targetSec` draws the target when the queues share one. */
export function ChartRow({
  series,
  times,
  targetSec,
  attention,
}: {
  series: DashSeries
  times: string[]
  targetSec?: number
  attention: AttentionItem[]
}) {
  const { say } = useStaffText()
  const targetMin = targetSec ? Math.round(targetSec / 60) : undefined
  return (
    <div className="fl-grid2">
      <Card role="region" aria-labelledby="fl-wchart">
        <Card.Header className="fl-card-h">
          <SectionTitle id="fl-wchart">{say('floor.db.waitingChart')}</SectionTitle>
          <div className="fl-legend" aria-hidden="true">
            <span data-line="people">{say('floor.db.l.people')}</span>
            <span data-line="longest">{say('floor.db.k.longest')}</span>
            {targetMin ? <span data-line="target">{say('floor.db.l.target', { n: targetMin })}</span> : null}
          </div>
        </Card.Header>
        <WaitChart
          people={series.waiting}
          longestMin={series.longestMin}
          targetMin={targetMin}
          times={times}
          nowLabel={say('floor.db.nowAxis')}
          peopleAxis={say('floor.db.l.people')}
          minAxis={say('floor.db.l.min')}
          label={say('floor.db.waitingChart')}
          hover={(i) =>
            `${times[i] ?? ''} · ${say('hall.tile.waiting', { n: series.waiting[i] ?? 0 })} · ${say('hall.min', { n: series.longestMin[i] ?? 0 })}`
          }
        />
      </Card>
      <AttentionCard items={attention} />
    </div>
  )
}

/** Served per quarter hour, waiting by branch or queue (and the average wait against
 *  the warning time), and the desks by state. */
export function TrendRow({
  series,
  times,
  byTitle,
  byRows,
  vsRows,
  desks,
  desksCaption,
}: {
  series: DashSeries
  times: string[]
  byTitle: string
  byRows: HBar[]
  vsRows: HBar[]
  desks: DeskCounts
  desksCaption: string
}) {
  const { say } = useStaffText()
  const range = (i: number) => `${times[i - 1] ?? ''} – ${i === times.length - 1 ? say('floor.db.nowSmall') : times[i]}`
  return (
    <div className="fl-grid3">
      <Card role="region" aria-labelledby="fl-served">
        <Card.Header className="fl-card-h">
          <SectionTitle id="fl-served" tip={say('floor.db.servedSub')}>
            {say('floor.db.served')}
          </SectionTitle>
        </Card.Header>
        <BarChart
          values={series.served}
          times={times}
          nowLabel={say('floor.db.nowAxis')}
          label={say('floor.db.served')}
          hover={(i) => `${range(i)} · ${series.served[i] ?? 0}`}
        />
      </Card>
      <Card role="region" aria-labelledby="fl-by">
        <Card.Header className="fl-card-h">
          <SectionTitle id="fl-by" tip={say('floor.db.waitingNow')}>
            {byTitle}
          </SectionTitle>
        </Card.Header>
        {byRows.length === 0 ? <p className="fl-quiet">{say('floor.db.nobodyWaiting')}</p> : <HBars rows={byRows} />}
        {vsRows.length > 0 && (
          <>
            <div className="fl-subhead">{say('floor.db.avgVsWarning')}</div>
            <HBars rows={vsRows} />
          </>
        )}
      </Card>
      <Card role="region" aria-labelledby="fl-donut">
        <Card.Header className="fl-card-h">
          <SectionTitle id="fl-donut">{say('floorBoard.desks')}</SectionTitle>
          <span className="fl-card-note">{desksCaption}</span>
        </Card.Header>
        <Donut
          label={say('floorBoard.desks')}
          centre={desks.serving + desks.free}
          sub={say('floor.db.open')}
          parts={[
            { label: say('floor.db.d.serving'), value: desks.serving, colour: 'var(--foreground)' },
            { label: say('floor.db.d.free'), value: desks.free, colour: 'var(--success)' },
            { label: say('floor.db.d.break'), value: desks.break, colour: 'var(--warning)' },
            { label: say('floor.db.d.closed'), value: desks.closed, colour: 'var(--border)' },
          ]}
        />
      </Card>
    </div>
  )
}

/** A wait as a table cell shows it: whole minutes. */
export function minWord(seconds: number, say: Say): string {
  return say('hall.min', { n: Math.round(seconds / 60) })
}
